import { POPULATION_MAX_CUSTOM_DAYS } from '@behuman/core-contract'
import { formatSlice } from './format'
import type { Measure, TrendPoint, TrendScope } from './hooks'

/** Pure helpers behind the Population Health sections (PD-1774), kept apart for testing. */

export type TrendDatum = TrendPoint & { label: string; days: number | null }

/**
 * The trend's slices as chart points, labelled with their real dates. A slice where nothing
 * closed keeps its place with a null value (a break in the line) rather than being dropped, so
 * the axis always shows every slice of the period.
 */
export const trendChartData = (points: TrendPoint[]): TrendDatum[] =>
  points.map((point) => ({ ...point, label: formatSlice(point), days: point.medianDays }))

export type SortKey =
  | 'rank'
  | 'label'
  | 'openPatients'
  | 'highPatients'
  | 'openedInPeriod'
  | 'days'
  | 'change'
  | 'status'

const STATUS_ORDER = { improving: 0, stable: 1, declining: 2 } as const

const sortValue: Record<SortKey, (m: Measure) => number | string | null> = {
  rank: (m) => m.rank,
  label: (m) => m.label,
  openPatients: (m) => m.openPatients,
  highPatients: (m) => m.highPatients,
  openedInPeriod: (m) => m.openedInPeriod,
  days: (m) => m.closureTime.current.medianDays,
  change: (m) => m.closureTime.changeDays,
  status: (m) => (m.status === null ? null : STATUS_ORDER[m.status]),
}

/** Sorts by one column; missing values (nothing resolved, no status) always go last. */
export function sortMeasures(measures: Measure[], key: SortKey, descending: boolean): Measure[] {
  const value = sortValue[key]
  return [...measures].sort((a, b) => {
    const x = value(a)
    const y = value(b)
    if (x === null || y === null) return x === y ? a.rank - b.rank : x === null ? 1 : -1
    const order = typeof x === 'string' ? x.localeCompare(y as string) : x - (y as number)
    return (descending ? -order : order) || a.rank - b.rank
  })
}

/**
 * The measure behind the trend's callout: the largest drop in median time to resolve (in whole
 * days, as shown), or when nothing got faster, the largest rise. None when no measure changed.
 */
export function supportingMeasure(measures: Measure[]): Measure | undefined {
  const changed = measures.filter((m) => m.closureTime.changeDays !== null)
  const days = (m: Measure) => Math.round(m.closureTime.changeDays!)
  const faster = changed.filter((m) => days(m) < 0)
  if (faster.length > 0) return faster.reduce((a, b) => (days(b) < days(a) ? b : a))
  const slower = changed.filter((m) => days(m) > 0)
  if (slower.length > 0) return slower.reduce((a, b) => (days(b) > days(a) ? b : a))
  return undefined
}

/** The measures a trend scope covers: all, one family group's, or the one measure. */
export const measuresInScope = (measures: Measure[], scope: TrendScope): Measure[] =>
  scope.measureSlug
    ? measures.filter((m) => m.slug === scope.measureSlug)
    : scope.groupSlug
      ? measures.filter((m) => m.group.slug === scope.groupSlug)
      : measures

/** Select options that always include the current value, even one the options don't list. */
export const withSelected = (
  data: { value: string; label: string }[],
  value: string | undefined,
): { value: string; label: string }[] =>
  value && !data.some((option) => option.value === value)
    ? [...data, { value, label: value }]
    : data

const DAY_MS = 24 * 60 * 60 * 1000
const dayTime = (day: string) => Date.parse(`${day}T00:00:00.000Z`)

/** Today's UTC calendar day: the last day the API accepts for a custom period. */
export const utcToday = (now: Date = new Date()): string => now.toISOString().slice(0, 10)

/** Calendar days in an inclusive range of 'YYYY-MM-DD' days. */
export const rangeDayCount = (start: string, end: string): number =>
  Math.round((dayTime(end) - dayTime(start)) / DAY_MS) + 1

const isDay = (day: string | null): day is string => {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return false
  const time = dayTime(day)
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === day
}

/**
 * A custom period's range when the API would accept it — real days, start ≤ end ≤ today
 * (UTC), at most POPULATION_MAX_CUSTOM_DAYS long — else undefined.
 */
export function customRange(
  start: string | null,
  end: string | null,
  now: Date = new Date(),
): { start: string; end: string } | undefined {
  if (!isDay(start) || !isDay(end) || start > end || end > utcToday(now)) return undefined
  return rangeDayCount(start, end) <= POPULATION_MAX_CUSTOM_DAYS ? { start, end } : undefined
}
